import React, {Component} from 'react';
import {addUserToOrg, getOrgMembers, removeUserFromOrg} from '../../services/orgService';
import ErrorAlert from "../../components/ErrorAlert";
import SectionCard from "../../patterns/SectionCard";
import {SimpleList, SimpleListRow} from "../../patterns/SimpleList";
import EmptyState from "../../patterns/EmptyState";
import {PanelEmptyIllustration} from "../../patterns/EmptyStateIllustrations";
import FormDialog from "../../patterns/FormDialog";
import ConfirmDialog from "../../patterns/datalist/ConfirmDialog";
import Toast from "../../patterns/Toast";
import TextInput from "../../patterns/form/TextInput";
import Banner from "../../patterns/Banner";
import styles from './OrgPanel.module.css';

export default class Members extends Component {
    constructor(props) {
        super(props);
        this.state = {
            members: null,
            emailToAdd: '',

            showCreateModal: false,
            addBusy: false,

            emailToDelete: null,
            removeBusy: false,

            addUserToOrgError: null,
            removeUserFromOrgError: null,
            persistentErrorMessage: null,
            toast: null,
        }
    }

    async componentDidMount() {
        try {
            const membersResponse = await getOrgMembers(this.props.org.id);
            this.setState({
                members: membersResponse.members,
            });
        } catch (error) {
            this.setState({
                persistentErrorMessage: `Problem getting members: ${error.toString()}`
            });
        }
    }

    cancelAddUser = () => {
        this.setState({showCreateModal: false, emailToAdd: '', addUserToOrgError: null});
    }

    addClicked = () => {
        this.setState({showCreateModal: true});
    }

    emailChanged = (event) => {
        this.setState({emailToAdd: event.target.value});
    }

    addUserToOrg = async () => {
        this.setState({addBusy: true});
        try {
            await addUserToOrg(this.props.org.id, this.state.emailToAdd);
            await this.refreshMembers();
            this.setState({
                showCreateModal: false,
                emailToAdd: '',
                addBusy: false,
                toast: {message: `${this.state.emailToAdd} added to org`}
            })
        } catch (e) {
            let message = `Problem adding user to org: ${e.toString()}`;
            if (e.response?.data?.error === 'Invalid value') message = `Invalid email address`;
            else if (e.response?.data?.error) message = e.response.data.error;
            this.setState({addUserToOrgError: message, addBusy: false});
        }
    }

    cancelDelete = () => {
        this.setState({emailToDelete: null, removeUserFromOrgError: null});
    }

    deleteClicked = (email) => {
        this.setState({emailToDelete: email})
    };

    removeUserFromOrg = async () => {
        const email = this.state.emailToDelete;
        this.setState({removeBusy: true});
        try {
            await removeUserFromOrg(this.props.org.id, email);
            await this.refreshMembers();
            this.setState({
                emailToDelete: null,
                removeBusy: false,
                toast: {message: `${email} removed from org`}
            })
        } catch (e) {
            let message = `Problem removing user from org: ${e.toString()}`;
            if (e.response?.data?.error === 'Invalid value') message = `Invalid email address`;
            else if (e.response?.data?.error) message = e.response.data.error;
            this.setState({removeUserFromOrgError: message, removeBusy: false});
        }
    }

    refreshMembers = async () => {
        const membersResponse = await getOrgMembers(this.props.org.id);
        this.setState({members: membersResponse.members});
    }

    dismissToast = () => this.setState({toast: null});

    render() {
        const {members, showCreateModal, emailToAdd, addBusy, addUserToOrgError,
            emailToDelete, removeBusy, removeUserFromOrgError, persistentErrorMessage, toast} = this.state;

        return (
            <div>
                <SectionCard
                    title="Members"
                    action={
                        <button type="button" className={styles.addButton} disabled={this.props.disabled}
                                onClick={this.addClicked}>
                            + Add member
                        </button>
                    }
                >
                    {!members && !persistentErrorMessage && <p className={styles.loading}>Loading members…</p>}
                    <ErrorAlert message={persistentErrorMessage} delay={0}/>
                    {!this.props.isOwner && members && (
                        <Banner tone="info">You cannot modify members. You are not the org owner.</Banner>
                    )}
                    {members && (members.length ? (
                        <SimpleList>
                            {members.map((member) => (
                                <SimpleListRow
                                    key={member.userId}
                                    primary={member.name}
                                    secondary={member.email}
                                    removeLabel={`Remove ${member.email} from org`}
                                    onRemove={this.props.disabled || member.userId === this.props.org.ownerId
                                        ? undefined
                                        : () => this.deleteClicked(member.email)}
                                />
                            ))}
                        </SimpleList>
                    ) : (
                        <EmptyState
                            size="panel"
                            illustration={<PanelEmptyIllustration/>}
                            title="No members yet"
                            description="Add people to this org so they can work on its pages."
                        />
                    ))}
                </SectionCard>

                <FormDialog
                    open={showCreateModal}
                    title={`Add a member to ${this.props.org.name}`}
                    onCancel={this.cancelAddUser}
                    onSubmit={this.addUserToOrg}
                    submitLabel="Add"
                    submitDisabled={!emailToAdd}
                    busy={addBusy}
                >
                    {(firstFieldRef) => (
                        <TextInput
                            ref={firstFieldRef}
                            id="add-member-email"
                            label="Email address"
                            type="email"
                            placeholder="name@company.com"
                            value={emailToAdd}
                            onChange={this.emailChanged}
                            error={addUserToOrgError}
                        />
                    )}
                </FormDialog>

                <ConfirmDialog
                    open={!!emailToDelete}
                    title="Remove member?"
                    body={`Are you sure you want to remove ${emailToDelete} from this org? They'll lose access to its pages.`}
                    confirmLabel="Remove"
                    onConfirm={this.removeUserFromOrg}
                    onCancel={this.cancelDelete}
                    busy={removeBusy}
                    error={removeUserFromOrgError}
                />

                {toast && <Toast message={toast.message} onDismiss={this.dismissToast}/>}
            </div>
        );
    }
}
